'use client';
import { useParams } from 'next/navigation';
import { createContext, useState, ReactNode, useEffect } from 'react';

import { getIsFollowById, unFollowAccount, followAccount } from '@/api/relationship/relationship';
import BaseButton from '@/components/baseButton/baseButton';
import { useUserInfoCompletedHook } from '@/features/userInfoCompleted/userInfoCompleted';
import { useValidatePhoneHook } from '@/features/validatePhone/validatePhone';
import { visitPofile } from '@/lib/websocket';
import { useBaseStore } from '@/store/base';
import { useChatStore } from '@/store/chat';
import { useNotifyStore } from '@/store/notify';
import { useUserStore } from '@/store/user';
import { CpInfoType } from '@/types/cpType';

import CpCoverPhoto from './components/cpCoverPhoto/cpCoverPhoto';
import CpInfoHeader from './components/cpInfoHeader/cpInfoHeader';
import ChatIcon from './img/chat.svg';
import FavoriteIcon from './img/favorite.svg';
import HeartIcon from './img/heart.svg';
// import PhoneIcon from './img/phone.svg';

import style from './layout.module.scss';

const CpInfoContext = createContext({} as CpInfoType);

function CpInfoLayoutContainer({
    cpInfoData,
    backHistory,
    children
}: {
    cpInfoData: CpInfoType;
    backHistory?: boolean;
    children: ReactNode;
}) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [secondRender, setSecondRender] = useState(false);
    const [isFollow, setIsFollow] = useState(false);
    const [cpInfo, setCpInfo] = useState<CpInfoType>(cpInfoData as CpInfoType);
    const params = useParams();
    const paramsCount = Object.keys(params).length;
    const isLogin = useUserStore.use.isLogin();
    const setLoginPopup = useBaseStore.use.setLoginPopup();
    const switchChat = useChatStore.use.switchChat();
    const setCurrentChat = useChatStore.use.setCurrentChat();
    const setSnackMessage = useNotifyStore.use.setSnackMessage();
    const userInfo = useUserStore.use.userInfo();

    const { checkUserInfoCompleted } = useUserInfoCompletedHook();
    const { checkValidatePhone } = useValidatePhoneHook();

    const installPWA = useBaseStore.use.installPWA();

    useEffect(() => {
        if (document.title.includes('404')) {
            document.title = 'WHyWHy';
        }
        setSecondRender(true);
    }, []);

    useEffect(() => {
        setCpInfo(cpInfoData);
    }, [cpInfoData]);

    useEffect(() => {
        if (cpInfo.user_id) visitPofile(cpInfo.user_id);
    }, [cpInfo]);

    useEffect(() => {
        const fetchIsFollow = async () => {
            const res = await getIsFollowById(cpInfoData.user_id);
            if (res.status === 200) {
                setIsFollow(res.data.is_follow);
            }
        };

        if (secondRender && isLogin && cpInfoData.user_id) {
            fetchIsFollow();
        }
    }, [isLogin, cpInfoData.user_id, secondRender]);

    const handleFavoriteBtn = () => {
        if (!isLogin) {
            setLoginPopup();
            return;
        }

        if (checkUserInfoCompleted() || checkValidatePhone()) {
            return;
        }

        if (userInfo.user_id === cpInfo.user_id) {
            setSnackMessage({ message: '不可追蹤自己！' });
            return;
        }

        if (isFollow) {
            unFavoriteCp();
        } else {
            favoriteCp();
        }
    };

    const unFavoriteCp = async () => {
        const res = await unFollowAccount(cpInfoData.user_id);
        if (res.status === 200) {
            setIsFollow(false);
            setSnackMessage({ message: '已取消追蹤' });
        }
    };

    const favoriteCp = async () => {
        const res = await followAccount(cpInfoData.user_id);
        if (res.status === 200) {
            setIsFollow(true);
            setSnackMessage({ message: '已追蹤' });
            setTimeout(() => {
                installPWA();
            }, 500);
        }
    };

    if (paramsCount > 1) {
        return children;
    }

    const handleChat = () => {
        if (!isLogin) {
            setLoginPopup();
            return;
        }

        if (checkUserInfoCompleted()) {
            return;
        }

        if (userInfo.user_id === cpInfo.user_id) {
            setSnackMessage({ message: '不可跟自己聊天！' });
            return;
        }
        setCurrentChat(cpInfoData);
        switchChat();
    };

    return (
        <CpInfoContext.Provider value={cpInfo}>
            <div className={style.cpInfoLayout}>
                <CpInfoHeader backHistory={backHistory} cpInfoData={cpInfo} />
                <div className={style.coverPhoto}>
                    <CpCoverPhoto coverList={cpInfo.cover} />
                </div>
                <div className={style.optionBar}>
                    <BaseButton
                        type={'normal'}
                        className={`${style.favoriteBtn} ${isFollow && style.isFollow}`}
                        onClick={handleFavoriteBtn}
                    >
                        {isFollow ? <HeartIcon /> : <FavoriteIcon />}
                    </BaseButton>
                    {/* <BaseButton type={'normal'} className={style.phoneBtn}>
                        <PhoneIcon /> 通話
                    </BaseButton> */}

                    <BaseButton type={'normal'} className={style.messageBtn} onClick={handleChat}>
                        <ChatIcon /> 聊天
                    </BaseButton>
                </div>
                {children}
            </div>
        </CpInfoContext.Provider>
    );
}

export { CpInfoLayoutContainer, CpInfoContext };
